import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import JSONbig from 'json-bigint';

import {
  adminPassword,
  adminUsername,
  barbersLimitedRegistrationApiUrl,
} from '../../config';
import { NewPartner } from '../../types/partner';

const response = [
  (data: any) => {
    if (typeof data === 'string') {
      try {
        data = JSONbig.parse(data);
      } catch (e) {
        console.error(e);
      }
    }
    return data;
  },
];

export const getPartnerByEmail = async (
  email: string | null,
  role: string | null,
  token: string
): Promise<AxiosResponse> => {
  const config: AxiosRequestConfig = {
    method: 'GET',
    url: `${barbersLimitedRegistrationApiUrl}/${role}/email`,
    params: {
      email,
    },
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    transformResponse: response,
  };
  return axios.request(config);
};

export const getNewPartners = async (): Promise<
  AxiosResponse<NewPartner[]>
> => {
  const config: AxiosRequestConfig = {
    method: 'GET',
    url: `${barbersLimitedRegistrationApiUrl}/partners/new`,
    headers: {
      'Content-Type': 'application/json',
    },
    auth: {
      username: adminUsername,
      password: adminPassword,
    },
    transformResponse: response,
  };
  return axios.request(config);
};

export const getShopById = async (
  shopId: bigint | null
): Promise<AxiosResponse> => {
  const config: AxiosRequestConfig = {
    method: 'GET',
    url: `${barbersLimitedRegistrationApiUrl}/shops/${shopId}`,
    headers: {
      'Content-Type': 'application/json',
    },
    auth: {
      username: adminUsername,
      password: adminPassword,
    },
    transformResponse: response,
  };
  return axios.request(config);
};

export const getBarberById = async (
  barberId: bigint | null
): Promise<AxiosResponse> => {
  const config: AxiosRequestConfig = {
    method: 'GET',
    url: `${barbersLimitedRegistrationApiUrl}/barbers/${barberId}`,
    headers: {
      'Content-Type': 'application/json',
    },
    auth: {
      username: adminUsername,
      password: adminPassword,
    },
    transformResponse: response,
  };
  return axios.request(config);
};

export const getShopBarberById = async (
  shopBarberId: bigint | null
): Promise<AxiosResponse> => {
  const config: AxiosRequestConfig = {
    method: 'GET',
    url: `${barbersLimitedRegistrationApiUrl}/shopBarbers/${shopBarberId}`,
    headers: {
      'Content-Type': 'application/json',
    },
    auth: {
      username: adminUsername,
      password: adminPassword,
    },
    transformResponse: response,
  };
  return axios.request(config);
};

export const getShopOwnerById = async (
  shopOwnerId: bigint | null
): Promise<AxiosResponse> => {
  const config: AxiosRequestConfig = {
    method: 'GET',
    url: `${barbersLimitedRegistrationApiUrl}/shopOwners/${shopOwnerId}`,
    headers: {
      'Content-Type': 'application/json',
    },
    auth: {
      username: adminUsername,
      password: adminPassword,
    },
    transformResponse: response,
  };
  return axios.request(config);
};

export const getShopSubscriptionByShopId = async (
  shopId: bigint | null,
  token: string
): Promise<AxiosResponse> => {
  const config: AxiosRequestConfig = {
    method: 'GET',
    url: `${barbersLimitedRegistrationApiUrl}/shopSubscriptions/${shopId}/byShopId`,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    transformResponse: response,
  };
  return axios.request(config);
};

export const getBarberSubscriptionByBarberId = async (
  barberId: bigint | null,
  token: string
): Promise<AxiosResponse> => {
  const config: AxiosRequestConfig = {
    method: 'GET',
    url: `${barbersLimitedRegistrationApiUrl}/barberSubscriptions/${barberId}/byBarberId`,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    transformResponse: response,
  };
  return axios.request(config);
};

export const retrieveCheckoutSession = async (
  sessionId: string,
  token: string
): Promise<AxiosResponse> => {
  const config: AxiosRequestConfig = {
    method: 'GET',
    url: `${barbersLimitedRegistrationApiUrl}/checkout-session/${sessionId}`,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    transformResponse: response,
  };
  return axios.request(config);
};

export const retrieveStripeCustomer = async (
  customerId: string,
  token: string
): Promise<AxiosResponse> => {
  const config: AxiosRequestConfig = {
    method: 'GET',
    url: `${barbersLimitedRegistrationApiUrl}/stripe/customers/${customerId}`,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    transformResponse: response,
  };
  return axios.request(config);
};

export const listCustomerPaymentMethods = async (
  customerId: string,
  token: string,
  limit = 10
): Promise<AxiosResponse> => {
  const config: AxiosRequestConfig = {
    method: 'GET',
    url: `${barbersLimitedRegistrationApiUrl}/stripe/customers/${customerId}/payment-methods`,
    params: {
      limit,
    },
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    transformResponse: response,
  };
  return axios.request(config);
};

export const retrieveStripeSubscription = async (
  subscriptionId: string,
  token: string
): Promise<AxiosResponse> => {
  const config: AxiosRequestConfig = {
    method: 'GET',
    url: `${barbersLimitedRegistrationApiUrl}/stripe/subscription/${subscriptionId}`,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    transformResponse: response,
  };
  return axios.request(config);
};

export const getGeoCode = async (address: string): Promise<AxiosResponse> => {
  const config: AxiosRequestConfig = {
    method: 'GET',
    url: `${barbersLimitedRegistrationApiUrl}/geocode`,
    params: { address },
    headers: {
      'Content-Type': 'application/json',
    },
    auth: {
      username: adminUsername,
      password: adminPassword,
    },
  };
  return axios.request(config);
};
